import { channelInfo } from '@op/components/ChannelLabel';
import { formatChartParamsDate } from '@op/components/ChartCommon/formatter';
import EChartCommon from '@op/components/EChartCommon';
import { textShowFun } from '@op/pages/Overview/KeyMetrics/MentionsSentiment/MetricsChart/utils';
import { getPercentageInfo } from '@op/utils';
import { getYAxisWithChannelIcon } from '@op/utils/chart';
import { dealWithOthersName } from '@op/utils/languageValue';
import { reviewsFormat, yAxisFormat } from '@op/utils/value';
import React, { useMemo } from 'react';
import { chartTitle } from '../../utils';
import { useIntl } from 'umi';

export default function SentimentBarChart(props: any) {
  const {
    chartType,
    type,
    chartData,
    filter,
    seriesConfig,
    tooltipConfig,
    title,
    titleTips,
    rightTitle,
    rightTitleTips,
    yAxisRightConfig,
    xAxisConfig,
  } = props;
  const { formatMessage } = useIntl();
  const eChartsRef = React.useRef(null as any);

  const options = useMemo(() => {
    const { dataY, total } = chartData;

    const rightYAxisData = yAxisRightConfig?.dataKey
      ? chartData?.[yAxisRightConfig?.dataKey]
      : total;

    let selected = {};

    if (
      seriesConfig?.some(({ dataKey }: { dataKey: string }) =>
        ['negativeMentions', 'neutralMentions', 'positiveMentions']?.includes(
          dataKey,
        ),
      )
    ) {
      const hasNegative = chartData?.negativeMentions?.some(
        ({ value }) => Math.abs(value) > 0,
      );
      const hasPositive = chartData?.positiveMentions?.some(
        ({ value }) => Math.abs(value) > 0,
      );

      selected = {
        Negative: true,
        Neutral: false,
        Positive: true,
      };
      if (!hasNegative && !hasPositive) {
        selected = {
          Negative: true,
          Neutral: true,
          Positive: true,
        };
      }
    }

    // const maxLabelWidth =
    //   Math.max(...(measureTextWidth(dataY, type) || [])) || 60;
    // const rounded = Math.round(maxLabelWidth / 10) * 10;
    // const width = (rounded > maxLabelWidth ? rounded : rounded + 10) + 15;
    // const rich = createChannelRichElement(dataY) || {};

    const { axisLabelFormat, ...axisLabelOptions } = xAxisConfig || {};

    return {
      grid: {
        left: 0,
        right: 0,
        contentLabel: true,
      },
      tooltip: {
        extraCssText: 'z-index:8',
        confine: true,
        trigger: 'axis',
        formatter: (params: any) => {
          const currentDateTotal = rightYAxisData?.[params[0].dataIndex] || 0;
          const sumText = formatMessage({
            id: tooltipConfig?.totalText || 'Total',
          });
          return formatChartParamsDate({
            params,
            total: currentDateTotal,
            textShowFun: textShowFun(formatMessage, {
              gameStoreShowMarker: true,
            }),
            beforeSort: false,
            // listTitleFunc: (item) => formatMessage({ id: item.seriesName }),
            valueFormat: (value: number) =>
              tooltipConfig?.formatValue?.(value) ||
              reviewsFormat(Math.abs(value), 2),
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                // if (!item.originValue) return null;
                const share = getPercentageInfo(
                  Math.abs(item.originValue),
                  total,
                  2,
                );
                return {
                  ...item,
                  share: share === '-' || share === '0%' ? '-' : share,
                };
              });
              if (tooltipConfig?.showTotal) {
                newRecords.unshift({
                  text: sumText,
                  value: reviewsFormat(total),
                  share: '100%',
                });
              }
              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const channelIcon =
                type === 'channel'
                  ? `<img src='${
                      channelInfo(params[0]?.name.toLocaleLowerCase() as any)
                        .img
                    }'/>`
                  : '';
              const columns = [
                {
                  key: 'text',
                  header: `<span class="echart-tooltip">${channelIcon}${dealWithOthersName(
                    params[0]?.name,
                    type,
                  )}<span>`,
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({
                    id: tooltipConfig?.title || 'Mentions',
                  }),
                },
              ];

              if (tooltipConfig?.showShare) {
                columns.push({
                  key: 'share',
                  align: 'right',
                  weight: 'bold',
                  header: formatMessage({ id: 'Share-1' }),
                });
              }

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
        axisPointer: {
          type: 'shadow',
        },
      },
      legend: {
        data: seriesConfig?.map((item) => ({
          name: item.name,
          itemStyle: { color: item?.color },
        })),
        selected,
        formatter(name: string) {
          return formatMessage({ id: name });
        },
      },
      xAxis: {
        type: 'value',
        splitLine: {
          show: true,
          lineStyle: { color: '#E5E5E5', type: 'dashed' },
        },
        axisLine: {
          show: true,
          lineStyle: { color: '#E5E5E5' },
        },
        axisLabel: {
          formatter: (value: number) =>
            axisLabelFormat?.(value) || yAxisFormat(Math.abs(value)), // 负值转成正值
        },
        boundaryGap: ['10%', 0],
        ...(axisLabelOptions || {}),
      },
      yAxis: [
        ...getYAxisWithChannelIcon({ data: dataY, formatMessage, type, corsMode: true }),
        {
          type: 'category',
          position: 'right',
          data: rightYAxisData,
          axisLabel: {
            formatter: (value) => {
              return `{value|${
                yAxisRightConfig?.formatValue(value) || reviewsFormat(value)
              }}`;
            },
            show: true,
            width: 40,
            rich: {
              value: {
                align: 'right',
              },
            },
            fontWeight: 400,
            fontSize: 12,
            lineHeight: 16,
            padding: [0, 0, 0, 0],
          },
          splitLine: {
            show: false,
            lineStyle: { color: '#E5E5E5', type: 'dashed' },
          },
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
          ...yAxisRightConfig,
        },
      ],
      series: seriesConfig?.map((val: any) => {
        const { dataKey, ...res } = val;
        return {
          ...res,
          data: chartData?.[dataKey],
        };
      }),
    };
  }, [chartData, formatMessage, seriesConfig, type]);

  const empty = useMemo(() => {
    return !chartData?.dataY?.length;
  }, [chartData]);

  return (
    <EChartCommon
      options={options}
      ref={eChartsRef}
      empty={empty}
      {...props}
      rightTitle={chartTitle({
        formatMessage,
        title: rightTitle,
        titleTipsKey: rightTitleTips,
      })}
    />
  );
}
